// Shareable-link support: the Typst source is gzipped + base64url-encoded and
// carried in the URL hash (`#src=…`). 100% client-side — nothing is uploaded.
// The profile photo is NOT included (too large for a URL); a shared link
// restores the text only, and the UI asks the recipient to re-upload a photo.

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function gzipBytes(input: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream("gzip");
  const writer = cs.writable.getWriter();
  void writer.write(input as BufferSource);
  void writer.close();
  return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

async function gunzipBytes(input: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("gzip");
  const writer = ds.writable.getWriter();
  void writer.write(input as BufferSource);
  void writer.close();
  return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

/** Gzip + base64url the source into a compact string for the URL hash. */
export async function encodeShareSource(source: string): Promise<string> {
  const gz = await gzipBytes(new TextEncoder().encode(source));
  return bytesToBase64Url(gz);
}

/** Inverse of {@link encodeShareSource}. Throws on malformed input. */
export async function decodeShareSource(param: string): Promise<string> {
  const bytes = await gunzipBytes(base64UrlToBytes(param));
  return new TextDecoder().decode(bytes);
}

/** Extract the `src` payload from a URL hash string, or null if absent. */
export function readShareParam(hash: string): string | null {
  const m = /[#&]src=([^&]+)/.exec(hash);
  return m ? m[1] : null;
}

/** Build a shareable absolute URL for the given encoded payload. */
export function buildShareUrl(origin: string, encoded: string): string {
  return `${origin}/#src=${encoded}`;
}
